/**
 * The timer's smart default for a session started late, early or on time (timer spec step 5, decided with Petr
 * 2026-10-15). The countdown counts the planned amount, and the block moves to when the student actually began; what
 * comes after decides how much of it fits:
 *
 * 1. Nothing after it: the whole plan.
 * 2. Study sessions in the way: they're pushed later, keeping the breaks the student's planning settings give, then with
 *    the breaks shrunk; only if that still runs into something fixed is this countdown shortened.
 * 3. A class, activity, own event or commute in the way: count down only until it, less the shortest break before it.
 *
 * How far a break may shrink depends on what follows it: before a study session or a commute, to nothing; before an
 * event with no commute, to 5 minutes (to get to a seat). Shovel never stops the timer: this is only where the countdown
 * aims, and the student can pick the other way (Change).
 */

export const MINUTE = 60 * 1000;
// A session over this long is deep work and earns the longer break after it (taskPlanning.ts).
const DEEP_WORK_MINUTES = 45;
// The shortest break before an event with no commute: time to get to a seat.
export const SEAT_MINUTES = 5;
// A countdown shortened below this isn't worth offering: stop at the next thing instead.
export const SHORTEST_COUNTDOWN_MINUTES = 5;

export type TimerCalendarItem = {
  id: string;
  /** A study session (moves), a class / activity / own event (fixed), or the commute before or after one (fixed). */
  kind: 'session' | 'fixed' | 'commute';
  start: number;
  end: number;
  title?: string;
  /** A session's task, and when it is due (ms); a session isn't moved past it (bump.ts). */
  taskId?: number;
  dueDate?: number;
  /** A session with time on it, ticked, or of a completed task stays where it is; undefined is movable. */
  movable?: boolean;
  /**
   * An Activity (Lunch, Workout, Me Time…): ▶ runs through it with under 15 minutes of study before it (bump.ts);
   * classes, commutes, own and imported events always stop it.
   */
  flexible?: boolean;
};

/** Whether a planned session may be moved to make room: no time on it, not ticked, its task not done. */
export const sessionCanMove = (session?: { isUsed?: boolean; used?: number; isTaskCompleted?: boolean } | null) =>
  !!session && !session.isUsed && !session.used && !session.isTaskCompleted;

/** The planning settings' breaks: after a long session, after a short one, and after a class or event. */
export type TimerBreaks = { deepBreakMinutes: number; smallBreakMinutes: number; eventBreakMinutes?: number };

/**
 * A session moved later, to `start`–`end`; `by` minutes later than it was ("Pushes the next session by 25m");
 * `shortenedBy` minutes shorter than it was, which go back to its task.
 */
export type SessionMove = { id: string; start: number; end: number; title?: string; by?: number; shortenedBy?: number };

/** The rest of a session split to fit (bump.ts): a new session of the same task, `start`–`end`. */
export type SessionPiece = { fromId: string; start: number; end: number; title?: string };

/** A session with no room left before it is due: it comes off, its minutes back to its task. */
export type SessionRemoval = { id: string; title?: string; minutes: number };

/**
 * The ways a start can go, as the "When I start late" setting and the Change menu name them; stopping at bedtime, or
 * for an Activity it would run through, is only ever a Change pick.
 */
export type StartWay = 'bump' | 'shortenThis' | 'shortenNext' | 'untilSession' | 'untilBedtime' | 'untilActivity';

export type TimerStartOption = {
  /** fits: the whole plan, nothing in the way; pushes: sessions after it move; stopsFor: shortened for something
   *  fixed; stopsAtSession: shortened to stop when the next session starts; full: the whole plan regardless;
   *  bumps: the rest of the day bumped down (bump.ts); shortensNext: the next session shortened to fit;
   *  stopsAtBedtime: shortened to stop at bedtime, the rest of the day bumped down. */
  kind: 'fits' | 'pushes' | 'stopsFor' | 'stopsAtSession' | 'full' | 'bumps' | 'shortensNext' | 'stopsAtBedtime';
  /** Which way it is, when it comes from planSmartStart (bump.ts). */
  way?: StartWay;
  /** How long the countdown counts; the block becomes this long, from now. */
  minutes: number;
  /** The other study sessions it moves. */
  moves: SessionMove[];
  /** What it stops for, when shortened. */
  stopFor?: { title?: string; at: number; commute: boolean };
  /** Minutes of the plan that go back to the task. */
  givesBack: number;
  /** Sessions split to fit: the pieces after the first, which keeps the session (bump.ts). */
  creates?: SessionPiece[];
  /** The started session's own minutes cut off by what it stops for, placed further on as new sessions of it (bump.ts). */
  rest?: SessionPiece[];
  /** An Activity it runs through: under way, or with under 15 minutes of study before it (bump.ts). */
  runsThrough?: { id: string; title?: string; at: number };
  /** Sessions with no room before they are due. */
  removes?: SessionRemoval[];
  /** Sessions moved to a later day: how many, and when the first of them starts. */
  later?: { count: number; at: number };
  /** Minutes the countdown runs past bedtime (the end of the awake time it starts in). */
  pastBedtime?: number;
  /** Bedtime, for a countdown that stops there. */
  bedtime?: number;
};

export type TimerStartPlan = { chosen: TimerStartOption; other?: TimerStartOption };

export const breakAfter = (minutes: number, breaks: TimerBreaks) =>
  minutes > DEEP_WORK_MINUTES ? breaks.deepBreakMinutes : breaks.smallBreakMinutes;

/**
 * Lays the sessions out after a block ending at `blockEnd`, each `gaps[i]` minutes after the one before (pushed only
 * if that's later than where it is). Returns their places and where the last ends.
 */
export const layOut = (blockEnd: number, sessions: TimerCalendarItem[], gaps: number[]) => {
  let at = blockEnd;
  const placed = sessions.map((session, index) => {
    const length = session.end - session.start;
    const start = Math.max(session.start, at + gaps[index] * MINUTE);
    at = start + length;
    return { session, start, end: start + length };
  });
  return { placed, end: at };
};

export const movesOf = (placed: { session: TimerCalendarItem; start: number; end: number }[]): SessionMove[] =>
  placed
    .filter(({ session, start }) => start !== session.start)
    .map(({ session, start, end }) => ({
      id: session.id,
      start,
      end,
      title: session.title,
      by: Math.round((start - session.start) / 60000)
    }));

/**
 * Where a session's countdown aims, started at `now` with `plannedMinutes` to do. `items` are the calendar's other
 * blocks (this session left out). Returns the chosen way and, when there is one, the other (the Change menu).
 */
export const planTimerStart = ({
  now,
  plannedMinutes,
  items,
  breaks
}: {
  now: number;
  plannedMinutes: number;
  items: TimerCalendarItem[];
  breaks: TimerBreaks;
}): TimerStartPlan => {
  const plan = Math.max(0, Math.round(plannedMinutes));
  const full: TimerStartOption = { kind: 'fits', minutes: plan, moves: [], givesBack: 0 };
  // What started already is the student's call (they chose to study instead); only what's still to come counts.
  const ahead = items.filter(item => item.start >= now).sort((a, b) => a.start - b.start);
  const fixed = ahead.find(item => item.kind !== 'session');
  const hardStop = fixed?.start ?? Infinity;
  const sessions = ahead.filter(item => item.kind === 'session' && item.start < hardStop);
  if (!sessions.length && !fixed) return { chosen: full };

  const blockEnd = (minutes: number) => now + minutes * MINUTE;
  const lengths = [plan, ...sessions.map(session => Math.round((session.end - session.start) / MINUTE))];
  // The break after each block: after this one before the first session, ..., after the last before the fixed thing.
  const wanted = lengths.map(length => breakAfter(length, breaks));
  const least = lengths.map((_, index) => (index < sessions.length ? 0 : fixed?.kind === 'commute' ? 0 : SEAT_MINUTES));
  const stopFor = fixed && { title: fixed.title, at: fixed.start, commute: fixed.kind === 'commute' };

  const fits = (gaps: number[], minutes: number) => {
    const { placed, end } = layOut(blockEnd(minutes), sessions, gaps);
    const lastGap = gaps[sessions.length] * MINUTE;
    return { placed, ok: !fixed || end + lastGap <= hardStop, end };
  };

  // Stop when the next session starts, moving nothing: the other way when sessions would be pushed.
  const untilSession = (): TimerStartOption | undefined => {
    if (!sessions.length) return undefined;
    const minutes = Math.floor((sessions[0].start - now) / MINUTE);
    return minutes >= 1 && minutes < plan
      ? { kind: 'stopsAtSession', minutes, moves: [], givesBack: plan - minutes }
      : undefined;
  };
  // The whole plan whatever is fixed after it, the sessions pushed past it: the other way when it's shortened.
  const skipping = (): TimerStartOption => {
    const { placed } = layOut(blockEnd(plan), sessions, wanted.slice(0, sessions.length));
    return { kind: 'full', minutes: plan, moves: movesOf(placed), stopFor, givesBack: 0 };
  };

  // 1. The whole plan with the breaks they want.
  const relaxed = fits(wanted, plan);
  if (relaxed.ok) {
    const moves = movesOf(relaxed.placed);
    if (!moves.length) return { chosen: full };
    return {
      chosen: { kind: 'pushes', minutes: plan, moves, givesBack: 0 },
      other: untilSession()
    };
  }

  // 2. The whole plan with the breaks shrunk: as short as they may go, then whatever room is left given back to them,
  // first to the break after this session.
  const tight = fits(least, plan);
  if (tight.ok) {
    let room = hardStop - (tight.end + least[sessions.length] * MINUTE);
    const gaps = least.map((min, index) => {
      const extra = Math.min(wanted[index] - min, Math.floor(room / MINUTE));
      room -= Math.max(0, extra) * MINUTE;
      return min + Math.max(0, extra);
    });
    const shrunk = fits(gaps, plan);
    const chosen = shrunk.ok ? shrunk : tight;
    const moves = movesOf(chosen.placed);
    return {
      chosen: moves.length
        ? { kind: 'pushes', minutes: plan, moves, givesBack: 0 }
        : { kind: 'fits', minutes: plan, moves: [], givesBack: 0 },
      other: moves.length ? untilSession() : skipping()
    };
  }

  // 3. Shorten this countdown: the sessions packed with the shortest breaks right up to the fixed thing.
  const overflow = Math.ceil((tight.end + least[sessions.length] * MINUTE - hardStop) / MINUTE);
  const shortened = plan - overflow;
  if (shortened >= SHORTEST_COUNTDOWN_MINUTES) {
    const packed = fits(least, shortened);
    return {
      chosen: {
        kind: 'stopsFor',
        minutes: shortened,
        moves: movesOf(packed.placed),
        stopFor,
        givesBack: plan - shortened
      },
      other: skipping()
    };
  }

  // No room even then: stop when the next thing starts, moving nothing.
  const atSession = untilSession();
  if (atSession) return { chosen: atSession, other: skipping() };
  const untilFixed = Math.floor((hardStop - now) / MINUTE) - least[sessions.length];
  if (untilFixed >= 1) {
    return {
      chosen: { kind: 'stopsFor', minutes: untilFixed, moves: [], stopFor, givesBack: plan - untilFixed },
      other: skipping()
    };
  }
  return { chosen: { ...full, kind: 'full', stopFor } };
};

/**
 * Countdown on a session already logged (Petr, 2026-10-16): its logged time is its plan, so the countdown extends the
 * block by `extendMinutes`, starting where the logged time ends (the block keeps its start). The added time follows the
 * late-start rules for what's after it: sessions pushed keeping their breaks, then the breaks shrunk, then shortened.
 */
export const planExtension = ({
  blockStart,
  usedMinutes,
  extendMinutes,
  items,
  breaks
}: {
  blockStart: number;
  usedMinutes: number;
  extendMinutes: number;
  items: TimerCalendarItem[];
  breaks: TimerBreaks;
}): TimerStartPlan =>
  planTimerStart({ now: blockStart + usedMinutes * MINUTE, plannedMinutes: extendMinutes, items, breaks });
